import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import deepmerge from 'deepmerge';
import { Box, Chip, IconButton, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import SearchIcon from '@mui/icons-material/Search';
import SmsIcon from '@mui/icons-material/Sms';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import CancelIcon from '@mui/icons-material/Cancel';

import useNotification from '../../../../../../core/hooks/use_notification';
import { getDefaultTheftRegistriesPaginationQueryValues } from '../../../../../../core/utils/api_utils';
import { cancelTheftRegistryNotificationData, createTheftRegistrySmsNotificationData, getPodsNotificationData, getTheftRegistriesNotificationData, updateTheftRegistryStatusNotificationData } from '../../../../../../core/utils/notification_utils';
import PaginationControls from '../../../../../common/pagination_controls';
import { getTheftRegistriesAction, getTheftRegistriesState } from '../../../../../../features/order/get_theft_registries';
import { getAllPodsAction, getAllPodsState } from '../../../../../../features/pod/get_all_pods';
import { getTheftRegistryStatus, getTheftRegistryStatusColor } from '../../../../../common/utils/theft_registry_utils';
import { Button, CustomIconButton } from '../../../../../common/button';
import { GET_CUSTOMER_THEFT_REGISTRY, GET_ROUTE_CUSTOMER_THEFT_REGISTRY_NEW, MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT } from '../../../../../../core/constants';
import { ApiRequestStatusEnum } from '../../../../../../core/enums/common/api';
import { updateTheftRegistryStatusAction } from '../../../../../../features/order/update_theft_registry_status';
import { TheftRegistryStatusEnum } from '../../../../../../core/enums/order/theft_registry';
import ConfirmDialog from '../../../../common/confirm_dialog';
import { createTheftSmsAction } from '../../../../../../features/order/create_theft_sms';
import { cancelTheftRegistryAction } from '../../../../../../features/order/cancel_theft_registry';

const initialQueryParams = {
    ...getDefaultTheftRegistriesPaginationQueryValues
};

const TheftRegistriesList = ({ customerId, tab }) => {
    const dispatch = useDispatch();
    const { showNotification, showErrorMessage } = useNotification();
    const navigate = useNavigate();

    const [queryParams, setQueryParams] = useState({ ...initialQueryParams, customerId });

    const getTheftRegistries = useSelector(getTheftRegistriesState);
    const getAllPods = useSelector(getAllPodsState);

    const [loadingSmsIds, setLoadingSmsIds] = useState([]);
    const [loadingStatusIds, setLoadingStatusIds] = useState([]);
    const [loadingCancelIds, setLoadingCancelIds] = useState([]);

    const refreshTheftRegistryList = useCallback(() => {
        dispatch(getTheftRegistriesAction(queryParams))
            .then(response => showErrorMessage(getTheftRegistriesNotificationData(response)));
        dispatch(getAllPodsAction())
            .then(response => showErrorMessage(getPodsNotificationData(response)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, queryParams]);

    useEffect(() => {
        refreshTheftRegistryList();
    }, [refreshTheftRegistryList]);

    const onUpdateTheftRegistryStatus = (id) => {
        setLoadingStatusIds(prev => [...prev, id]);
        dispatch(updateTheftRegistryStatusAction({ id, status: TheftRegistryStatusEnum.FINISHED.value }))
            .then(response => {
                showNotification(updateTheftRegistryStatusNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshTheftRegistryList();
            })
            .finally(() => {
                setLoadingStatusIds(prev => prev.filter(item => item !== id));
            });
    };

    const onCancelTheftRegistry = (id) => {
        setLoadingCancelIds(prev => [...prev, id]);
        dispatch(cancelTheftRegistryAction(id))
            .then(response => {
                showNotification(cancelTheftRegistryNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshTheftRegistryList();
            })
            .finally(() => {
                setLoadingCancelIds(prev => prev.filter(item => item !== id));
            });
    };

    const onSendSms = (id) => {
        setLoadingSmsIds(prev => [...prev, id]);
        dispatch(createTheftSmsAction(id))
            .then(response => showNotification(createTheftRegistrySmsNotificationData(response)))
            .finally(() => {
                setLoadingSmsIds(prev => prev.filter(item => item !== id));
            });
    };

    return (
        <Box>

            <Stack direction="row" spacing={ 2 } mb={ 4 } alignItems="center">
                <Button
                    onClick={ () => navigate(GET_ROUTE_CUSTOMER_THEFT_REGISTRY_NEW(customerId), { state: { tab } }) }>
                    Dodaj wpis do kartoteki
                </Button>
            </Stack>

            { getTheftRegistries?.loading && (
                <Box mb={ 2 }>
                    <LinearProgress/>
                </Box>
            ) }

            { getTheftRegistries?.data?.content && (
                <Box>
                    <TableContainer component={ Paper } variant="outlined">
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Data kradzieży</TableCell>
                                    <TableCell>Punkt sprzedaży</TableCell>
                                    <TableCell align="right">Wartość</TableCell>
                                    <TableCell align="center">Częściowa kradzież</TableCell>
                                    <TableCell align="center">Wysłano SMS</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                    <TableCell align="right" sx={ { minWidth: 160 } }></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                { getTheftRegistries?.data?.content?.map(theftRegistry => (
                                    <TableRow key={ theftRegistry?.id } hover>
                                        <TableCell sx={ { fontWeight: 600 } }>
                                            T{ theftRegistry?.id }
                                        </TableCell>
                                        <TableCell>
                                            { moment(theftRegistry?.theftDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={ { maxWidth: 250 } } noWrap title={
                                                getAllPods?.data?.map(pod => ({
                                                    id: pod?.id,
                                                    label: `${ pod?.indoorPartner?.name } (${ pod?.description }) - ${ pod?.device?.serviceId || 'brak urządzenia' }`
                                                }))
                                                    .find(pod => pod?.id === theftRegistry?.podId)?.label || 'Brak'
                                            }>
                                                { getAllPods?.data?.map(pod => ({
                                                    id: pod?.id,
                                                    label: `${ pod?.indoorPartner?.name } (${ pod?.description }) - ${ pod?.device?.serviceId || 'brak urządzenia' }`
                                                }))
                                                    .find(pod => pod?.id === theftRegistry?.podId)?.label || 'Brak' }
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={ { fontWeight: 500 } }>
                                            { theftRegistry?.shopAdminTotalSellingPrice.toFixed(2) } { theftRegistry?.currency }
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={ theftRegistry?.partialTheft ? "TAK" : "NIE" }
                                                color="secondary"
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={ theftRegistry?.smsSent ? `TAK (${ theftRegistry?.smsSentCount })` : "NIE" }
                                                color={ theftRegistry?.smsSent ? "success" : "default" }
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={ getTheftRegistryStatus(theftRegistry?.status) }
                                                color={ getTheftRegistryStatusColor(theftRegistry?.status) }
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" justifyContent="flex-end" spacing={ 1 }>
                                                { theftRegistry?.status === TheftRegistryStatusEnum.IN_REALIZATION.value && (
                                                    <>
                                                        <ConfirmDialog
                                                            isLoading={ loadingSmsIds.includes(theftRegistry?.id) }
                                                            maxWidth="sm"
                                                            title={ theftRegistry?.smsSent ? 'Czy na pewno chcesz ponownie wysłać wiadomość sms?' : 'Czy na pewno chcesz wysłać wiadomość sms?' }
                                                            secondaryAction={ () => {
                                                            } }
                                                            secondaryText="Anuluj"
                                                            primaryAction={ () => onSendSms(theftRegistry?.id) }
                                                            primaryText="Wyślij"
                                                            displayIcon
                                                            iconTitle="Wyślij SMS"
                                                            icon={ <SmsIcon fontSize="small"/> }
                                                        />
                                                        <CustomIconButton
                                                            title="Opłacono"
                                                            isLoading={ loadingStatusIds.includes(theftRegistry?.id) }
                                                            onClick={ () => onUpdateTheftRegistryStatus(theftRegistry?.id) }
                                                            icon={ <CreditScoreIcon fontSize="small"/> }
                                                        />
                                                        <CustomIconButton
                                                            title="Anuluj"
                                                            isLoading={ loadingCancelIds.includes(theftRegistry?.id) }
                                                            onClick={ () => onCancelTheftRegistry(theftRegistry?.id) }
                                                            icon={ <CancelIcon fontSize="small"/> }
                                                        />
                                                    </>
                                                ) }
                                                <Tooltip title="Szczegóły">
                                                    <IconButton
                                                        size="small"
                                                        onClick={ () => navigate(GET_CUSTOMER_THEFT_REGISTRY(customerId, theftRegistry?.id),
                                                            {
                                                                state: {
                                                                    tab,
                                                                    incompleteOrderId: theftRegistry?.incompleteOrderId,
                                                                    theftDate: theftRegistry?.theftDate,
                                                                    podId: theftRegistry?.podId
                                                                }
                                                            }) }
                                                    >
                                                        <SearchIcon/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                )) }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box mt={ 3 } display="flex" justifyContent="center">
                        <PaginationControls
                            currentPage={ getTheftRegistries?.data?.pageable?.pageNumber }
                            totalPages={ getTheftRegistries?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                </Box>
            ) }
        </Box>
    );
};

export default TheftRegistriesList;